import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import { useAuthed } from '../hooks/useAuthed';
import { gymApi } from '../api/gymApi';
import type { WorkoutSession } from '../../../shared/types';

const fmtDate = (secs: number) =>
  new Date(secs * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

/** Read-only view of a past workout, with a link into each exercise's history. */
export default function SessionDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const authed = useAuthed();
  const [session, setSession] = useState<WorkoutSession | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    gymApi.getWorkout(sessionId, authed)
      .then(s => { if (!cancelled) setSession(s); })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load workout'); });
    return () => { cancelled = true; };
  }, [sessionId, authed]);

  return (
    <>
      <SiteHeader back={{ label: 'History', to: '/history' }} />
      <main className="page page--narrow">
        {error && <p className="error">{error}</p>}
        {!session && !error && <p className="muted">Loading…</p>}

        {session && (
          <>
            <h1>{session.dayLabel}</h1>
            <p className="muted">
              {session.completedAt ? fmtDate(session.completedAt) : fmtDate(session.startedAt)}
              {' · '}{session.status}
            </p>

            {session.exercises.map(ex => {
              const working = ex.sets.filter(s => !s.warmup && s.completed);
              if (ex.skipped) {
                return (
                  <section key={ex.index} className="past-exercise">
                    <h3>{ex.exerciseName}</h3>
                    <p className="muted">Skipped</p>
                  </section>
                );
              }
              return (
                <section key={ex.index} className="past-exercise">
                  <h3 className="past-exercise-head">
                    <span>{ex.exerciseName}</span>
                    <button
                      type="button"
                      className="btn-quiet btn-xs"
                      onClick={() => navigate(`/exercise/${encodeURIComponent(ex.exerciseName)}`)}
                    >
                      History
                    </button>
                  </h3>
                  {working.length === 0 ? (
                    <p className="muted">No sets logged</p>
                  ) : (
                    <ul className="past-set-list">
                      {working.map(s => (
                        <li key={s.setNumber}>
                          <span className="muted">Set {s.setNumber}</span>
                          <span>{s.weight ?? '—'} × {s.reps ?? '—'}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              );
            })}
          </>
        )}
      </main>
    </>
  );
}
